import { useCallback, useEffect, useState } from "react";
import {
  approveModeration,
  excludeThread,
  fetchExcludedThreads,
  fetchModeration,
  rejectModeration,
  fetchPublishQueueStatus,
  publishAllQueued,
  publishQueueNow,
  importJsonPayload,
  fetchRagChunksStats,
  restoreThread,
  type ExcludedThread,
  type ImportJsonResult,
  type ModerationItem,
  type PublishQueueStatus,
  type RagChunksStats,
} from "@/api/admin";
import { AdminPlanningFeed } from "@/components/AdminPlanningFeed";
import { AdminArticleSocial } from "@/components/AdminArticleSocial";
import { markAdminSession } from "@/utils/adminSession";

type Tab = "moderation" | "threads" | "import" | "planning" | "articles";

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>("moderation");
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [excluded, setExcluded] = useState<ExcludedThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState("");
  const [threadReason, setThreadReason] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [importText, setImportText] = useState("");
  const [importResult, setImportResult] = useState<ImportJsonResult | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [queueLastMonth, setQueueLastMonth] = useState(true);
  const [hideChatSource, setHideChatSource] = useState(false);
  const [publishStatus, setPublishStatus] = useState<PublishQueueStatus | null>(
    null
  );
  const [publishNowBusy, setPublishNowBusy] = useState(false);
  const [planningRefresh, setPlanningRefresh] = useState(0);
  const [articlesPending, setArticlesPending] = useState(0);
  const [ragStats, setRagStats] = useState<RagChunksStats | null>(null);

  const formatBytes = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const reloadRagStats = useCallback(async () => {
    try {
      setRagStats(await fetchRagChunksStats());
      markAdminSession();
    } catch {
      /* не блокируем остальные вкладки */
    }
  }, []);

  const reloadModeration = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchModeration("pending");
      markAdminSession();
      setItems(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadExcluded = useCallback(async () => {
    try {
      setExcluded(await fetchExcludedThreads());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const reloadPublishStatus = useCallback(async () => {
    try {
      setPublishStatus(await fetchPublishQueueStatus());
      markAdminSession();
    } catch {
      /* статус очереди не критичен для остальных вкладок */
    }
  }, []);

  useEffect(() => {
    if (tab === "moderation") reloadModeration();
    else if (tab === "threads") reloadExcluded();
    else if (tab === "import" || tab === "planning") {
      reloadPublishStatus();
      if (tab === "import") reloadRagStats();
    }
  }, [tab, reloadModeration, reloadExcluded, reloadPublishStatus, reloadRagStats]);

  useEffect(() => {
    if (tab !== "import") return;
    reloadPublishStatus();
    reloadRagStats();
    const timer = window.setInterval(() => {
      reloadPublishStatus();
      reloadRagStats();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [tab, reloadPublishStatus, reloadRagStats]);

  const onApprove = async (id: number) => {
    setBusyId(id);
    try {
      await approveModeration(id);
      await reloadModeration();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (id: number) => {
    setBusyId(id);
    try {
      await rejectModeration(id);
      await reloadModeration();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onExclude = async () => {
    const id = parseInt(threadId.trim(), 10);
    if (!id || id < 1) {
      setError("Укажите thread_root_id (число из RAG chunk)");
      return;
    }
    setError(null);
    try {
      await excludeThread(id, threadReason.trim());
      setThreadId("");
      setThreadReason("");
      await reloadExcluded();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onImport = async () => {
    const raw = importText.trim();
    if (!raw) {
      setError("Вставьте JSON (bundle messages+rag_chunks или Telegram result.json)");
      return;
    }
    try {
      JSON.parse(raw);
    } catch {
      setError("Некорректный JSON");
      return;
    }
    setImportBusy(true);
    setError(null);
    setImportResult(null);
    try {
      const r = await importJsonPayload(raw, queueLastMonth, hideChatSource);
      setImportResult(r);
      await reloadRagStats();
      if (r.publish_queue) {
        setPublishStatus(r.publish_queue);
      } else {
        await reloadPublishStatus();
      }
      if ((r.feed_queued ?? 0) > 0) {
        setTab("planning");
        setPlanningRefresh((n) => n + 1);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImportBusy(false);
    }
  };

  const onImportFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      setImportText(text);
      setError(null);
    };
    reader.onerror = () => setError("Не удалось прочитать файл");
    reader.readAsText(file, "utf-8");
  };

  const onRestore = async (id: number) => {
    try {
      await restoreThread(id);
      await reloadExcluded();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Админка ai.cleverence.ru</h1>
        <nav className="admin-tabs">
          <button
            type="button"
            className={tab === "moderation" ? "active" : ""}
            onClick={() => setTab("moderation")}
          >
            Модерация ({items.length})
          </button>
          <button
            type="button"
            className={tab === "threads" ? "active" : ""}
            onClick={() => setTab("threads")}
          >
            Исключённые треды
          </button>
          <button
            type="button"
            className={tab === "import" ? "active" : ""}
            onClick={() => setTab("import")}
          >
            Импорт JSON
          </button>
          <button
            type="button"
            className={tab === "planning" ? "active" : ""}
            onClick={() => setTab("planning")}
          >
            Планирование ({publishStatus?.feed_queued_count ?? "…"})
          </button>
          <button
            type="button"
            className={tab === "articles" ? "active" : ""}
            onClick={() => setTab("articles")}
          >
            Статьи ({articlesPending})
          </button>
          <a href="/" className="admin-link-main">
            ← К чату
          </a>
        </nav>
      </header>

      {error ? <p className="admin-error">{error}</p> : null}

      {tab === "planning" ? (
        <AdminPlanningFeed refreshToken={planningRefresh} />
      ) : tab === "articles" ? (
        <AdminArticleSocial
          pendingCount={articlesPending}
          onPendingCountChange={setArticlesPending}
        />
      ) : tab === "import" ? (
        <section className="admin-section">
          <p className="admin-muted">
            JSON попадает в <strong>feed.sqlite</strong> (лента) и{" "}
            <strong>rag_chunks.jsonl</strong> (поиск и ответы бота). Формат: пакет ETL{" "}
            <code>{"{ messages, rag_chunks }"}</code> или экспорт Telegram{" "}
            <code>result.json</code> (поле <code>name</code> — заголовок чата; в RAG
            попадёт как домен). Можно явно:{" "}
            <code>chat_title</code>, <code>chat_id</code>,{" "}
            <code>domain_label</code>.
          </p>
          {ragStats ? (
            <div className="admin-queue-stats">
              <p>
                <strong>RAG</strong> ({ragStats.file_exists ? formatBytes(ragStats.file_bytes) : "нет файла"}
                ): всего <strong>{ragStats.total}</strong> чанков — Telegram{" "}
                <strong>{ragStats.telegram}</strong>, статьи <strong>{ragStats.articles}</strong>
                {ragStats.other > 0 ? (
                  <>
                    , прочие <strong>{ragStats.other}</strong>
                  </>
                ) : null}
              </p>
              <p className="admin-muted">
                <code>{ragStats.rag_chunks_path}</code>
              </p>
            </div>
          ) : null}
          {publishStatus ? (
            <div className="admin-queue-stats">
              <p>
                <strong>В ленте (опубликовано):</strong>{" "}
                {publishStatus.feed_published_count}
                {" · "}
                <strong>В очереди:</strong> {publishStatus.feed_queued_count}
              </p>
              {publishStatus.feed_queued_count > 0 ? (
                <>
                  <p className="admin-muted">
                    Публикация по одному каждые{" "}
                    {publishStatus.queue_interval_minutes} мин (случайно). Ночью (
                    {publishStatus.night_hours}) пауза.
                    {publishStatus.is_night_now ?
                      " Сейчас ночь — очередь ждёт утра."
                    : publishStatus.minutes_until_next != null ?
                      ` Следующее автоматически ~ через ${publishStatus.minutes_until_next} мин.`
                    : null}
                  </p>
                  <p className="admin-muted">
                    После снятия с очереди сообщение появляется в ленте с{" "}
                    <strong>текущим временем публикации</strong> (внизу у «последних»),
                    не с датой из исходного чата.
                  </p>
                  <button
                    type="button"
                    className="admin-btn"
                    disabled={publishNowBusy}
                    onClick={async () => {
                      setPublishNowBusy(true);
                      setError(null);
                      try {
                        const r = await publishAllQueued();
                        if (r.queue) setPublishStatus(r.queue);
                        else await reloadPublishStatus();
                        if (
                          r.status === "published_all" &&
                          (r.published_count ?? 0) > 0
                        ) {
                          setImportResult({
                            source: "publish_queue_all",
                            feed_upserted: r.published_count ?? 0,
                            feed_published_immediate: r.published_count ?? 0,
                            feed_queued: r.remaining_queued ?? 0,
                            queue_last_month: true,
                            feed_published_count:
                              r.queue?.feed_published_count ?? 0,
                            feed_queued_count: r.remaining_queued ?? 0,
                            publish_queue: r.queue ?? publishStatus,
                            rag_chunks_added: 0,
                            rag_chunks_skipped: 0,
                            rag_chunks_path: "",
                            etl: {
                              published_ids: r.published_message_ids,
                            },
                          });
                        }
                      } catch (e) {
                        setError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setPublishNowBusy(false);
                      }
                    }}
                  >
                    {publishNowBusy ?
                      "Публикуем…"
                    : "Опубликовать всё из очереди"}
                  </button>
                  <button
                    type="button"
                    className="admin-btn"
                    disabled={publishNowBusy || publishStatus.is_night_now}
                    onClick={async () => {
                      setPublishNowBusy(true);
                      setError(null);
                      try {
                        const r = await publishQueueNow();
                        if (r.queue) setPublishStatus(r.queue);
                        else await reloadPublishStatus();
                        if (r.status === "published") {
                          const d = r.published_message_date ?? "";
                          const dateHint =
                            d.length >= 10 ? d.slice(0, 10).split("-").reverse().join(".") : d;
                          setImportResult({
                            source: "publish_queue",
                            feed_upserted: 0,
                            feed_published_immediate: 1,
                            feed_queued: r.remaining_queued ?? 0,
                            queue_last_month: true,
                            feed_published_count:
                              r.queue?.feed_published_count ?? 0,
                            feed_queued_count: r.remaining_queued ?? 0,
                            publish_queue: r.queue ?? publishStatus,
                            rag_chunks_added: 0,
                            rag_chunks_skipped: 0,
                            rag_chunks_path: "",
                            etl: {
                              published_id: r.published_message_id,
                              date: r.published_message_date,
                              already_in_feed: r.already_in_feed,
                              hint: r.already_in_feed
                                ? `Снято с очереди #${r.published_message_id}. Было в ленте раньше — обновите чат (Ctrl+F5).`
                                : `Опубликовано #${r.published_message_id} (${dateHint}). Смотрите внизу ленты у «последних».`,
                            },
                          });
                        }
                      } catch (e) {
                        setError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setPublishNowBusy(false);
                      }
                    }}
                  >
                    {publishNowBusy ?
                      "Публикуем…"
                    : "Опубликовать следующее сейчас"}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
          <label className="admin-queue-option">
            <input
              type="checkbox"
              checked={queueLastMonth}
              onChange={(e) => setQueueLastMonth(e.target.checked)}
            />
            <span>
              Очередь публикации для сообщений за последние{" "}
              {publishStatus?.last_month_days ?? 30} дней (старше — сразу в ленту;
              из очереди — с датой публикации «сейчас»)
            </span>
          </label>
          <label className="admin-queue-option">
            <input
              type="checkbox"
              checked={hideChatSource}
              onChange={(e) => setHideChatSource(e.target.checked)}
            />
            <span>
              Не показывать название исходного чата рядом с именем автора в ленте
              (для этого импорта; RAG по-прежнему знает чат)
            </span>
          </label>
          <div className="admin-import-actions">
            <label className="admin-file-label">
              Файл JSON
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => onImportFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
          <textarea
            className="admin-import-textarea"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={`{\n  "name": "Маркировка. Радиоэлектроника",\n  "id": 2236997005,\n  "messages": [...]\n}\n\nили Telegram result.json с полем name`}
            spellCheck={false}
          />
          <button
            type="button"
            className="admin-btn approve"
            disabled={importBusy}
            onClick={onImport}
          >
            {importBusy ? "Импорт…" : "Импортировать в ленту и RAG"}
          </button>
          {importResult ? (
            <pre className="admin-import-result">
              {JSON.stringify(importResult, null, 2)}
            </pre>
          ) : null}
        </section>
      ) : tab === "moderation" ? (
        <section className="admin-section">
          {loading ? <p>Загрузка…</p> : null}
          {!loading && items.length === 0 ? (
            <p className="admin-muted">Нет записей в очереди.</p>
          ) : null}
          <ul className="admin-list">
            {items.map((item) => (
              <li key={item.id} className="admin-card">
                <div className="admin-meta">
                  #{item.id} · {item.created_at} · session {item.session_id.slice(0, 8)}…
                </div>
                <div className="admin-block">
                  <strong>Вопрос</strong>
                  <p>{item.user_message}</p>
                </div>
                <div className="admin-block">
                  <strong>Ответ бота</strong>
                  <p>{item.bot_answer}</p>
                </div>
                <div className="admin-actions">
                  <button
                    type="button"
                    className="admin-btn approve"
                    disabled={busyId === item.id}
                    onClick={() => onApprove(item.id)}
                  >
                    Одобрить в ленту
                  </button>
                  <button
                    type="button"
                    className="admin-btn reject"
                    disabled={busyId === item.id}
                    onClick={() => onReject(item.id)}
                  >
                    Отклонить
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section className="admin-section">
          <div className="admin-exclude-form">
            <label>
              thread_root_id
              <input
                value={threadId}
                onChange={(e) => setThreadId(e.target.value)}
                placeholder="например 12345"
              />
            </label>
            <label>
              Причина (необязательно)
              <input
                value={threadReason}
                onChange={(e) => setThreadReason(e.target.value)}
              />
            </label>
            <button type="button" className="admin-btn reject" onClick={onExclude}>
              Скрыть тред из ленты и поиска
            </button>
          </div>
          <ul className="admin-list compact">
            {excluded.map((row) => (
              <li key={row.thread_root_id} className="admin-row">
                <span>
                  Тред {row.thread_root_id} · {row.excluded_at}
                  {row.reason ? ` · ${row.reason}` : ""}
                </span>
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => onRestore(row.thread_root_id)}
                >
                  Восстановить
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
